/**
 * Table Metadata Modal Component
 * Editable form for table metadata (Owner, SLA, Tags, Metadata, Quality Rules)
 * Does NOT show schema/columns
 */

import React, { useState, useEffect } from 'react';
import { DraggableModal } from '@/components/common/DraggableModal';
import { useModelStore } from '@/stores/modelStore';
import { useUIStore } from '@/stores/uiStore';
import { useSDKModeStore } from '@/services/sdk/sdkMode';
import type { Table, Owner, SLA, Role, SupportChannel, Pricing, TeamMember } from '@/types/table';
import {
  getSourceTopic,
  SOURCE_TOPIC_KEY,
  CATALOG_KEY,
  SCHEMA_KEY,
  getCatalog,
  getSchema,
  RESOURCE_TYPE_KEY,
  getResourceType,
} from '@/utils/customProperties';
import type { ResourceType } from '@/utils/customProperties';

export interface TableMetadataModalProps {
  table: Table | null;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (updates: Partial<Table>) => void;
}

export const TableMetadataModal: React.FC<TableMetadataModalProps> = ({
  table,
  isOpen,
  onClose,
  onSave,
}) => {
  const { updateTable, updateTableRemote, selectedDomainId } = useModelStore();
  const { addToast } = useUIStore();
  const { mode } = useSDKModeStore();

  const [owner, setOwner] = useState<Owner | undefined>(undefined);
  const [roles, setRoles] = useState<Role[]>([]);
  const [support, setSupport] = useState<SupportChannel[]>([]);
  const [pricing, setPricing] = useState<Pricing | undefined>(undefined);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [sla, setSLA] = useState<SLA | undefined>(undefined);
  const [tags, setTags] = useState<string[]>([]);
  const [tagsInput, setTagsInput] = useState('');
  const [metadata, setMetadata] = useState<Record<string, unknown>>({});
  const [internalMetadata, setInternalMetadata] = useState<Record<string, unknown>>({});
  const [qualityRules, setQualityRules] = useState<Record<string, unknown> | unknown[]>({});
  const [status, setStatus] = useState<string>('');
  const [sourceTopic, setSourceTopic] = useState<string>('');
  const [catalog, setCatalog] = useState<string>('');
  const [schema, setSchema] = useState<string>('');
  const [resourceType, setResourceType] = useState<ResourceType | undefined>(undefined);
  // Managed DQ check state — table-level
  const [freshnessEnabled, setFreshnessEnabled] = useState(false);
  const [freshnessColumn, setFreshnessColumn] = useState<string>('');
  const [freshnessThreshold, setFreshnessThreshold] = useState<number>(1440);
  const [rowCountEnabled, setRowCountEnabled] = useState(true);
  const [rowCountBoundsEnabled, setRowCountBoundsEnabled] = useState(true);
  const [rowCountBoundsMin, setRowCountBoundsMin] = useState<number>(500);
  const [rowCountBoundsMax, setRowCountBoundsMax] = useState<number>(100000);
  const [rowCountBoundsDateColumn, setRowCountBoundsDateColumn] =
    useState<string>('operation_date');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Internal metadata keys that should not be shown in the UI
  const INTERNAL_METADATA_KEYS = [
    'system_id',
    'indexes',
    'status',
    'customProperties',
    'apiVersion',
    'version',
    'kind',
  ];

  // Valid ODCS status values
  const STATUS_OPTIONS = ['proposed', 'draft', 'active', 'deprecated', 'retired'] as const;

  /** Names/identifiers of managed table-level DQ rules hidden from the custom JSON editor */
  const MANAGED_RULE_NAMES = new Set(['table_is_not_empty', 'row_count_within_bounds']);

  /** Returns true when a rule is managed by the dedicated UI controls */
  const isManagedRule = (r: any): boolean => {
    if (r.type === 'library' && r.metric === 'freshness') return true;
    if (r.name && MANAGED_RULE_NAMES.has(r.name)) return true;
    return false;
  };

  // Helper to get status from customProperties
  const getStatusFromCustomProperties = (
    customProps: Array<{ property: string; value: unknown }> | undefined
  ): string => {
    if (!customProps || !Array.isArray(customProps)) return '';
    const statusProp = customProps.find((p) => p.property === 'status');
    return typeof statusProp?.value === 'string' ? statusProp.value : '';
  };

  // Initialize form when table changes
  useEffect(() => {
    if (table) {
      setOwner(table.owner || {});
      setRoles(table.roles || []);
      setSupport(table.support || []);
      setPricing(table.pricing || {});
      setTeam(table.team || []);
      setSLA(table.sla || {});
      // Filter out dm_level tags - these are managed by the Data Level dropdown, not the tags input
      const editableTags = (table.tags || []).filter(
        (tag) => !tag.toLowerCase().startsWith('dm_level:')
      );
      setTags(editableTags);
      setTagsInput(editableTags.join(', '));

      // Separate internal metadata from user-editable metadata
      const tableMetadata = table.metadata || {};
      const internal: Record<string, unknown> = {};
      const userEditable: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(tableMetadata)) {
        if (INTERNAL_METADATA_KEYS.includes(key)) {
          internal[key] = value;
        } else {
          userEditable[key] = value;
        }
      }
      setInternalMetadata(internal);
      setMetadata(userEditable);

      // Parse quality rules and extract managed DQ checks
      const rawRules = table.quality_rules;
      const rulesArray = Array.isArray(rawRules) ? rawRules : [];
      const hasManagedRules = rulesArray.some((r: any) => isManagedRule(r));

      // --- Freshness ---
      const existingFreshness = rulesArray.find(
        (r: any) => r.type === 'library' && r.metric === 'freshness'
      ) as any;
      if (existingFreshness) {
        setFreshnessEnabled(true);
        setFreshnessColumn(existingFreshness.arguments?.column || '');
        setFreshnessThreshold(existingFreshness.mustBeGreaterThan ?? 60);
      } else {
        setFreshnessEnabled(false);
        setFreshnessColumn('');
        setFreshnessThreshold(1440);
      }

      // --- Row count (table_is_not_empty) ---
      const existingRowCount = rulesArray.find((r: any) => r.name === 'table_is_not_empty') as any;
      setRowCountEnabled(existingRowCount ? true : !hasManagedRules ? true : false);

      // --- Row count within bounds ---
      const existingBounds = rulesArray.find(
        (r: any) => r.name === 'row_count_within_bounds'
      ) as any;
      if (existingBounds) {
        setRowCountBoundsEnabled(true);
        // Parse min/max from the SQL query pattern "BETWEEN X AND Y"
        const boundsMatch = existingBounds.query?.match(/BETWEEN\s+(\d+)\s+AND\s+(\d+)/i);
        setRowCountBoundsMin(boundsMatch ? parseInt(boundsMatch[1], 10) : 500);
        setRowCountBoundsMax(boundsMatch ? parseInt(boundsMatch[2], 10) : 100000);
        // Parse date column from "WHERE {col} = CURRENT_DATE()"
        const dateColMatch = existingBounds.query?.match(/WHERE\s+(\w+)\s*=/i);
        setRowCountBoundsDateColumn(dateColMatch?.[1] || 'operation_date');
      } else {
        setRowCountBoundsEnabled(!hasManagedRules ? true : false);
        setRowCountBoundsMin(500);
        setRowCountBoundsMax(100000);
        setRowCountBoundsDateColumn('operation_date');
      }

      // Store non-managed rules for the custom JSON editor
      const customRules = rulesArray.filter((r: any) => !isManagedRule(r));
      setQualityRules(
        customRules.length > 0 ? customRules : rawRules && !Array.isArray(rawRules) ? rawRules : {}
      );
      // Read status from customProperties (ODCS compliant)
      setStatus(getStatusFromCustomProperties(table.customProperties));
      setSourceTopic(getSourceTopic(table.customProperties) || '');
      setCatalog(getCatalog(table.customProperties) || '');
      setSchema(getSchema(table.customProperties) || '');
      setResourceType(getResourceType(table.customProperties));
      setHasUnsavedChanges(false);
    }
  }, [table]);

  const handleSave = async () => {
    if (!table) return;

    setIsSaving(true);
    try {
      // Filter out roles without a role name (required field)
      const validRoles = roles.filter((r) => r.role && r.role.trim().length > 0);

      // Filter out support channels without required fields
      const validSupport = support.filter(
        (s) => s.channel && s.channel.trim().length > 0 && s.url && s.url.trim().length > 0
      );

      // Filter out team members without at least username or name
      const validTeam = team.filter(
        (t) => (t.username && t.username.trim().length > 0) || (t.name && t.name.trim().length > 0)
      );

      // Build customProperties with managed keys (ODCS compliant)
      const updatedCustomProperties: Array<{ property: string; value: unknown }> = [
        // Keep existing customProperties except managed keys
        ...(table.customProperties || []).filter(
          (p) =>
            p.property !== 'status' &&
            p.property !== SOURCE_TOPIC_KEY &&
            p.property !== CATALOG_KEY &&
            p.property !== SCHEMA_KEY &&
            p.property !== RESOURCE_TYPE_KEY
        ),
        // Add status if set
        ...(status ? [{ property: 'status', value: status }] : []),
        // Add source_topic if set
        ...(sourceTopic.trim() ? [{ property: SOURCE_TOPIC_KEY, value: sourceTopic.trim() }] : []),
        // Add catalog if set
        ...(catalog.trim() ? [{ property: CATALOG_KEY, value: catalog.trim() }] : []),
        // Add schema if set
        ...(schema.trim() ? [{ property: SCHEMA_KEY, value: schema.trim() }] : []),
        // Add resource_type if not default (table)
        ...(resourceType ? [{ property: RESOURCE_TYPE_KEY, value: resourceType }] : []),
      ];

      const updates: Partial<Table> = {
        customProperties: updatedCustomProperties.length > 0 ? updatedCustomProperties : undefined,
        owner: owner && (owner.name || owner.email) ? owner : undefined,
        roles: validRoles.length > 0 ? validRoles : undefined,
        support: validSupport.length > 0 ? validSupport : undefined,
        pricing:
          pricing &&
          (pricing.priceAmount !== undefined || pricing.priceCurrency || pricing.priceUnit)
            ? pricing
            : undefined,
        team: validTeam.length > 0 ? validTeam : undefined,
        sla:
          sla &&
          (sla.latency !== undefined ||
            sla.uptime !== undefined ||
            sla.response_time !== undefined ||
            sla.error_rate !== undefined ||
            sla.update_frequency)
            ? sla
            : undefined,
        // Preserve dm_level tag from original table (managed by Data Level dropdown)
        tags: (() => {
          const dmLevelTag = (table.tags || []).find((t) =>
            t.toLowerCase().startsWith('dm_level:')
          );
          const allTags = dmLevelTag ? [...tags, dmLevelTag] : tags;
          return allTags.length > 0 ? allTags : undefined;
        })(),
        // Merge user-editable metadata with preserved internal metadata
        metadata: (() => {
          const merged = { ...internalMetadata, ...metadata };
          return Object.keys(merged).length > 0 ? merged : undefined;
        })(),
        quality_rules: (() => {
          // Collect all managed DQ rules that are enabled
          const managedRules: Record<string, unknown>[] = [];

          if (rowCountEnabled) {
            managedRules.push({
              type: 'library',
              name: 'table_is_not_empty',
              description: 'Table must contain at least one row',
              dimension: 'completeness',
              metric: 'rowCount',
              mustBeGreaterThan: 0,
              severity: 'error',
            });
          }

          if (rowCountBoundsEnabled && rowCountBoundsDateColumn) {
            managedRules.push({
              type: 'sql',
              name: 'row_count_within_bounds',
              description: 'Daily row count should be within expected range',
              dimension: 'completeness',
              query: `SELECT CASE WHEN COUNT(*) BETWEEN ${rowCountBoundsMin} AND ${rowCountBoundsMax} THEN 0 ELSE 1 END FROM {table} WHERE ${rowCountBoundsDateColumn} = DATEADD(DAY, -1, CURRENT_DATE())`,
              mustBe: 0,
              severity: 'warning',
            });
          }

          if (freshnessEnabled && freshnessColumn) {
            managedRules.push({
              type: 'library',
              name: 'timeliness_check',
              description: `Data in ${freshnessColumn} should not be older than ${freshnessThreshold} minutes`,
              metric: 'freshness',
              dimension: 'timeliness',
              arguments: { column: freshnessColumn },
              mustBeGreaterThan: freshnessThreshold,
              severity: 'warning',
            });
          }

          // Merge with custom rules from the JSON editor
          const customRulesArray = Array.isArray(qualityRules)
            ? qualityRules
            : Object.keys(qualityRules as Record<string, unknown>).length > 0
              ? [qualityRules]
              : [];
          const allRules = [...managedRules, ...customRulesArray];
          return allRules.length > 0 ? allRules : undefined;
        })(),
        // Generate column-level quality rules (column_exists + null_ratio per column)
        columns: (table.columns || []).map((col) => {
          const existingRules = Array.isArray(col.quality_rules) ? col.quality_rules : [];
          // Strip any previously-managed column-level rules
          const customColRules = existingRules.filter(
            (r: any) =>
              !(
                r.type === 'custom' && r.implementation?.expectation === 'expect_column_to_exist'
              ) && !(r.type === 'library' && r.metric === 'nullValues')
          );
          const managedColRules: Record<string, unknown>[] = [];
          // Every column gets a column_exists check
          managedColRules.push({
            type: 'custom',
            engine: 'great-expectations',
            name: `column_${col.name.replace(/\s+/g, '_')}_exists`,
            description: `Column '${col.name}' must exist in the table`,
            dimension: 'consistency',
            severity: 'error',
            implementation: { expectation: 'expect_column_to_exist' },
          });
          // Nullable columns get a null ratio check — preserve any existing per-column threshold
          if (col.nullable) {
            const existingNullRule = existingRules.find(
              (r: any) => r.type === 'library' && r.metric === 'nullValues'
            ) as any;
            const threshold = existingNullRule?.mustBeLessThan ?? 0.1;
            managedColRules.push({
              type: 'library',
              name: `${col.name.replace(/\s+/g, '_')}_null_ratio`,
              description: `${col.name} column should not be more than ${Math.round(threshold * 100)}% null`,
              dimension: 'completeness',
              metric: 'nullValues',
              arguments: { column: col.name },
              mustBeLessThan: threshold,
              severity: 'warning',
            });
          }
          const allColRules = [...managedColRules, ...customColRules];
          return { ...col, quality_rules: allColRules.length > 0 ? allColRules : undefined };
        }),
        last_modified_at: new Date().toISOString(),
      };

      // If onSave callback is provided, use it (allows parent to merge with pending changes)
      if (onSave) {
        onSave(updates);
      } else {
        // Fallback: Update store directly (when used standalone)
        updateTable(table.id, updates);

        // Update remote if online
        if (mode === 'online' && selectedDomainId) {
          try {
            await updateTableRemote(selectedDomainId, table.id, updates);
          } catch (error) {
            console.warn('Remote update failed, changes saved locally', error);
          }
        }
      }

      addToast({
        type: 'success',
        message: 'Table metadata saved successfully',
      });

      setHasUnsavedChanges(false);
      onClose();
    } catch (error) {
      addToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to save metadata',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTagsInputChange = (value: string) => {
    setTagsInput(value);
    // Parse tags from input
    // Split on ", " (comma + space) to separate different tags
    // This allows "env:prod,staging" to be one tag, but "env:prod, product:food" to be two tags
    const newTags = value
      .split(/, /)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    setTags(newTags);
    setHasUnsavedChanges(true);
  };

  if (!table) return null;

  const isEditable = table.primary_domain_id === selectedDomainId;

  return (
    <DraggableModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Table Metadata: ${table.name}`}
      size="lg"
      zIndex={100}
      initialPosition={{
        x: Math.max(50, window.innerWidth / 2 - 400),
        y: Math.max(50, window.innerHeight / 2 - 300),
      }}
    >
      <div className="space-y-4 max-h-[80vh] overflow-y-auto">
        {!isEditable && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              This table belongs to another domain and cannot be edited here.
            </p>
          </div>
        )}

        {/* Basic Info */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Basic Information</h3>
          <div className="space-y-1 text-sm">
            <div>
              <span className="font-medium text-gray-600">Name:</span>{' '}
              <span className="text-gray-900">{table.name}</span>
            </div>
            {table.alias && (
              <div>
                <span className="font-medium text-gray-600">Alias:</span>{' '}
                <span className="text-gray-900">{table.alias}</span>
              </div>
            )}
            {table.description && (
              <div>
                <span className="font-medium text-gray-600">Description:</span>{' '}
                <span className="text-gray-900">{table.description}</span>
              </div>
            )}
            {table.data_level && (
              <div>
                <span className="font-medium text-gray-600">Data Level:</span>{' '}
                <span className="text-gray-900 capitalize">{table.data_level}</span>
              </div>
            )}
          </div>
          {/* Status Dropdown */}
          <div className="mt-3">
            <label htmlFor="table-status" className="block text-sm font-medium text-gray-600 mb-1">
              Status
            </label>
            {isEditable ? (
              <select
                id="table-status"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Select status...</option>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-gray-900 capitalize">{status || 'Not set'}</span>
            )}
          </div>
          {/* Source Topic */}
          <div className="mt-3">
            <label
              htmlFor="table-source-topic"
              className="block text-sm font-medium text-gray-600 mb-1"
            >
              Source Topic
            </label>
            {isEditable ? (
              <input
                id="table-source-topic"
                type="text"
                value={sourceTopic}
                onChange={(e) => {
                  setSourceTopic(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="e.g., order-events, user-profiles"
              />
            ) : (
              <span className="text-gray-900">{sourceTopic || 'Not set'}</span>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Groups tables from the same schema or topic
            </p>
          </div>
          {/* Catalog */}
          <div className="mt-3">
            <label htmlFor="table-catalog" className="block text-sm font-medium text-gray-600 mb-1">
              Catalog
            </label>
            {isEditable ? (
              <input
                id="table-catalog"
                type="text"
                value={catalog}
                onChange={(e) => {
                  setCatalog(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="e.g., raw, curated, analytics"
              />
            ) : (
              <span className="text-gray-900">{catalog || 'Not set'}</span>
            )}
            <p className="mt-1 text-xs text-gray-500">Database catalog for this table</p>
          </div>
          {/* Schema */}
          <div className="mt-3">
            <label htmlFor="table-schema" className="block text-sm font-medium text-gray-600 mb-1">
              Schema
            </label>
            {isEditable ? (
              <input
                id="table-schema"
                type="text"
                value={schema}
                onChange={(e) => {
                  setSchema(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="e.g., sales, finance, marketing"
              />
            ) : (
              <span className="text-gray-900">{schema || 'Not set'}</span>
            )}
            <p className="mt-1 text-xs text-gray-500">Database schema for this table</p>
          </div>
          {/* Resource Type */}
          <div className="mt-3">
            <label
              htmlFor="table-resource-type"
              className="block text-sm font-medium text-gray-600 mb-1"
            >
              Resource Type
            </label>
            {isEditable ? (
              <select
                id="table-resource-type"
                value={resourceType || 'table'}
                onChange={(e) => {
                  const val = e.target.value as ResourceType;
                  setResourceType(val === 'table' ? undefined : val);
                  setHasUnsavedChanges(true);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="table">Table</option>
                <option value="view">View</option>
                <option value="materialized_view">Materialized View</option>
              </select>
            ) : (
              <span className="text-gray-900 capitalize">
                {resourceType ? resourceType.replace('_', ' ') : 'Table'}
              </span>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Whether this resource is a table, view, or materialized view
            </p>
          </div>
        </div>

        {/* Tags */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Tags</h3>
            <div className="relative group">
              <button
                type="button"
                className="w-4 h-4 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 text-xs font-semibold"
                title="Tag format help"
              >
                ?
              </button>
              <div className="invisible group-hover:visible absolute left-0 top-6 z-50 w-80 bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-xs">
                <h4 className="font-semibold text-gray-900 mb-2">Tag Formats</h4>
                <div className="space-y-2 text-gray-700">
                  <div>
                    <span className="font-medium">Simple:</span>
                    <code className="ml-2 px-1 bg-gray-100 rounded">production</code>
                    <p className="text-xs text-gray-600 mt-1">Single word tag</p>
                  </div>
                  <div>
                    <span className="font-medium">Keyword:</span>
                    <code className="ml-2 px-1 bg-gray-100 rounded">env:production</code>
                    <p className="text-xs text-gray-600 mt-1">Key-value pair</p>
                  </div>
                  <div>
                    <span className="font-medium">Keyword with list:</span>
                    <code className="ml-2 px-1 bg-gray-100 rounded">env:production,staging</code>
                    <p className="text-xs text-gray-600 mt-1">
                      Key with multiple values (no spaces)
                    </p>
                  </div>
                  <div className="pt-2 border-t border-gray-200 mt-2">
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">Multiple tags:</span> Separate with comma +
                      space
                    </p>
                    <code className="block mt-1 px-1 bg-gray-100 rounded">
                      env:production, product:food
                    </code>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {isEditable ? (
            <div>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => handleTagsInputChange(e.target.value)}
                placeholder="e.g., env:production, product:food or pii, sensitive"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {tags.map((tag, idx) => (
                    <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm">
              {tags.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {tags.map((tag, idx) => (
                    <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-gray-500">No tags</span>
              )}
            </div>
          )}
        </div>

        {/* Owner Information */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Owner</h3>
          {isEditable ? (
            <div className="space-y-2">
              <div>
                <label
                  htmlFor="owner-name"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Name
                </label>
                <input
                  id="owner-name"
                  type="text"
                  value={owner?.name || ''}
                  onChange={(e) => {
                    setOwner({ ...owner, name: e.target.value });
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Owner name"
                />
              </div>
              <div>
                <label
                  htmlFor="owner-email"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Email
                </label>
                <input
                  id="owner-email"
                  type="email"
                  value={owner?.email || ''}
                  onChange={(e) => {
                    setOwner({ ...owner, email: e.target.value });
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="owner@example.com"
                />
              </div>
              <div>
                <label
                  htmlFor="owner-team"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Team
                </label>
                <input
                  id="owner-team"
                  type="text"
                  value={owner?.team || ''}
                  onChange={(e) => {
                    setOwner({ ...owner, team: e.target.value });
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Team name"
                />
              </div>
              <div>
                <label
                  htmlFor="owner-role"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Role
                </label>
                <input
                  id="owner-role"
                  type="text"
                  value={owner?.role || ''}
                  onChange={(e) => {
                    setOwner({ ...owner, role: e.target.value });
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="e.g., Data Owner, Data Steward"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-1 text-sm">
              {owner && (owner.name || owner.email || owner.team || owner.role) ? (
                <>
                  {owner.name && (
                    <div>
                      <span className="font-medium text-gray-600">Name:</span>{' '}
                      <span className="text-gray-900">{owner.name}</span>
                    </div>
                  )}
                  {owner.email && (
                    <div>
                      <span className="font-medium text-gray-600">Email:</span>{' '}
                      <span className="text-gray-900">{owner.email}</span>
                    </div>
                  )}
                  {owner.team && (
                    <div>
                      <span className="font-medium text-gray-600">Team:</span>{' '}
                      <span className="text-gray-900">{owner.team}</span>
                    </div>
                  )}
                  {owner.role && (
                    <div>
                      <span className="font-medium text-gray-600">Role:</span>{' '}
                      <span className="text-gray-900">{owner.role}</span>
                    </div>
                  )}
                </>
              ) : (
                <span className="text-gray-500">No owner information</span>
              )}
            </div>
          )}
        </div>

        {/* Roles Information */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Roles</h3>
          {isEditable ? (
            <div className="space-y-3">
              {roles.map((role, index) => (
                <div key={index} className="p-3 border border-gray-200 rounded-md bg-gray-50">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-xs font-semibold text-gray-700">Role {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newRoles = roles.filter((_, i) => i !== index);
                        setRoles(newRoles);
                        setHasUnsavedChanges(true);
                      }}
                      className="text-red-600 hover:text-red-800 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <label
                        htmlFor={`role-${index}-name`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Role Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id={`role-${index}-name`}
                        type="text"
                        value={role.role || ''}
                        onChange={(e) => {
                          const newRoles = [...roles];
                          newRoles[index] = { ...role, role: e.target.value };
                          setRoles(newRoles);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="IAM role name"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`role-${index}-description`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Description
                      </label>
                      <textarea
                        id={`role-${index}-description`}
                        value={role.description || ''}
                        onChange={(e) => {
                          const newRoles = [...roles];
                          newRoles[index] = { ...role, description: e.target.value || undefined };
                          setRoles(newRoles);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Description of the IAM role and its permissions"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`role-${index}-access`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Access Type
                      </label>
                      <input
                        id={`role-${index}-access`}
                        type="text"
                        value={role.access || ''}
                        onChange={(e) => {
                          const newRoles = [...roles];
                          newRoles[index] = { ...role, access: e.target.value || undefined };
                          setRoles(newRoles);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="e.g., read-only, read-write, admin"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`role-${index}-first-approvers`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        1st Level Approvers
                      </label>
                      <input
                        id={`role-${index}-first-approvers`}
                        type="text"
                        value={
                          Array.isArray(role.firstLevelApprovers)
                            ? role.firstLevelApprovers.join(', ')
                            : role.firstLevelApprovers || ''
                        }
                        onChange={(e) => {
                          const newRoles = [...roles];
                          const value = e.target.value.trim();
                          newRoles[index] = {
                            ...role,
                            firstLevelApprovers: value
                              ? value.includes(',')
                                ? value.split(',').map((s) => s.trim())
                                : value
                              : undefined,
                          };
                          setRoles(newRoles);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Comma-separated names"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`role-${index}-second-approvers`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        2nd Level Approvers
                      </label>
                      <input
                        id={`role-${index}-second-approvers`}
                        type="text"
                        value={
                          Array.isArray(role.secondLevelApprovers)
                            ? role.secondLevelApprovers.join(', ')
                            : role.secondLevelApprovers || ''
                        }
                        onChange={(e) => {
                          const newRoles = [...roles];
                          const value = e.target.value.trim();
                          newRoles[index] = {
                            ...role,
                            secondLevelApprovers: value
                              ? value.includes(',')
                                ? value.split(',').map((s) => s.trim())
                                : value
                              : undefined,
                          };
                          setRoles(newRoles);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Comma-separated names"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`role-${index}-custom-props`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Custom Properties (JSON)
                      </label>
                      <textarea
                        id={`role-${index}-custom-props`}
                        value={
                          role.customProperties
                            ? JSON.stringify(role.customProperties, null, 2)
                            : ''
                        }
                        onChange={(e) => {
                          const newRoles = [...roles];
                          try {
                            const parsed = e.target.value.trim()
                              ? JSON.parse(e.target.value)
                              : undefined;
                            newRoles[index] = { ...role, customProperties: parsed };
                            setRoles(newRoles);
                            setHasUnsavedChanges(true);
                          } catch {
                            // Invalid JSON, don't update
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
                        placeholder='{"key": "value"}'
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setRoles([...roles, { role: '' }]);
                  setHasUnsavedChanges(true);
                }}
                className="w-full px-3 py-2 text-sm border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
              >
                + Add Role
              </button>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              {roles.length > 0 ? (
                roles.map((role, index) => (
                  <div key={index} className="p-3 border border-gray-200 rounded-md">
                    <div className="font-medium text-gray-900 mb-1">{role.role}</div>
                    {role.description && (
                      <div className="text-gray-600 mb-1">{role.description}</div>
                    )}
                    {role.access && (
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">Access:</span> {role.access}
                      </div>
                    )}
                    {role.firstLevelApprovers && (
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">1st Level Approvers:</span>{' '}
                        {Array.isArray(role.firstLevelApprovers)
                          ? role.firstLevelApprovers.join(', ')
                          : role.firstLevelApprovers}
                      </div>
                    )}
                    {role.secondLevelApprovers && (
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">2nd Level Approvers:</span>{' '}
                        {Array.isArray(role.secondLevelApprovers)
                          ? role.secondLevelApprovers.join(', ')
                          : role.secondLevelApprovers}
                      </div>
                    )}
                    {role.customProperties && Object.keys(role.customProperties).length > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        <span className="font-medium">Custom Properties:</span>{' '}
                        <pre className="inline-block">
                          {JSON.stringify(role.customProperties, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <span className="text-gray-500">No roles defined</span>
              )}
            </div>
          )}
        </div>

        {/* Support and Communication Channels */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            Support and Communication Channels
          </h3>
          {isEditable ? (
            <div className="space-y-3">
              {support.map((channel, index) => (
                <div key={index} className="p-3 border border-gray-200 rounded-md bg-gray-50">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-xs font-semibold text-gray-700">Channel {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newSupport = support.filter((_, i) => i !== index);
                        setSupport(newSupport);
                        setHasUnsavedChanges(true);
                      }}
                      className="text-red-600 hover:text-red-800 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <label
                        htmlFor={`channel-${index}-name`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Channel Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id={`channel-${index}-name`}
                        type="text"
                        value={channel.channel || ''}
                        onChange={(e) => {
                          const newSupport = [...support];
                          newSupport[index] = { ...channel, channel: e.target.value };
                          setSupport(newSupport);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Channel name or identifier"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`channel-${index}-url`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        id={`channel-${index}-url`}
                        type="url"
                        value={channel.url || ''}
                        onChange={(e) => {
                          const newSupport = [...support];
                          newSupport[index] = { ...channel, url: e.target.value };
                          setSupport(newSupport);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="https://example.com or mailto:email@example.com"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`channel-${index}-description`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Description
                      </label>
                      <input
                        id={`channel-${index}-description`}
                        type="text"
                        value={channel.description || ''}
                        onChange={(e) => {
                          const newSupport = [...support];
                          newSupport[index] = {
                            ...channel,
                            description: e.target.value || undefined,
                          };
                          setSupport(newSupport);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Description of the channel"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label
                          htmlFor={`channel-${index}-tool`}
                          className="block text-xs font-medium text-gray-600 mb-1"
                        >
                          Tool
                        </label>
                        <select
                          id={`channel-${index}-tool`}
                          value={channel.tool || ''}
                          onChange={(e) => {
                            const newSupport = [...support];
                            newSupport[index] = { ...channel, tool: e.target.value || undefined };
                            setSupport(newSupport);
                            setHasUnsavedChanges(true);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="">Select tool...</option>
                          <option value="email">Email</option>
                          <option value="slack">Slack</option>
                          <option value="teams">Teams</option>
                          <option value="discord">Discord</option>
                          <option value="ticket">Ticket</option>
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor={`channel-${index}-scope`}
                          className="block text-xs font-medium text-gray-600 mb-1"
                        >
                          Scope
                        </label>
                        <select
                          id={`channel-${index}-scope`}
                          value={channel.scope || ''}
                          onChange={(e) => {
                            const newSupport = [...support];
                            newSupport[index] = {
                              ...channel,
                              scope: e.target.value as
                                | 'interactive'
                                | 'announcements'
                                | 'issues'
                                | undefined,
                            };
                            setSupport(newSupport);
                            setHasUnsavedChanges(true);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="">Select scope...</option>
                          <option value="interactive">Interactive</option>
                          <option value="announcements">Announcements</option>
                          <option value="issues">Issues</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor={`channel-${index}-invitation`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Invitation URL
                      </label>
                      <input
                        id={`channel-${index}-invitation`}
                        type="url"
                        value={channel.invitationUrl || ''}
                        onChange={(e) => {
                          const newSupport = [...support];
                          newSupport[index] = {
                            ...channel,
                            invitationUrl: e.target.value || undefined,
                          };
                          setSupport(newSupport);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Invitation URL for requesting or subscribing"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setSupport([...support, { channel: '', url: '' }]);
                  setHasUnsavedChanges(true);
                }}
                className="w-full px-3 py-2 text-sm border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
              >
                + Add Support Channel
              </button>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              {support.length > 0 ? (
                support.map((channel, index) => (
                  <div key={index} className="p-3 border border-gray-200 rounded-md">
                    <div className="font-medium text-gray-900 mb-1">{channel.channel}</div>
                    <div className="text-blue-600 hover:underline mb-1">
                      <a href={channel.url} target="_blank" rel="noopener noreferrer">
                        {channel.url}
                      </a>
                    </div>
                    {channel.description && (
                      <div className="text-gray-600 mb-1">{channel.description}</div>
                    )}
                    <div className="flex gap-2 text-xs text-gray-500">
                      {channel.tool && (
                        <span>
                          <span className="font-medium">Tool:</span> {channel.tool}
                        </span>
                      )}
                      {channel.scope && (
                        <span>
                          <span className="font-medium">Scope:</span> {channel.scope}
                        </span>
                      )}
                    </div>
                    {channel.invitationUrl && (
                      <div className="text-xs text-blue-600 hover:underline mt-1">
                        <a href={channel.invitationUrl} target="_blank" rel="noopener noreferrer">
                          Invitation URL
                        </a>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <span className="text-gray-500">No support channels defined</span>
              )}
            </div>
          )}
        </div>

        {/* Pricing */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Pricing</h3>
          {isEditable ? (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label
                    htmlFor="pricing-amount"
                    className="block text-xs font-medium text-gray-600 mb-1"
                  >
                    Price Amount
                  </label>
                  <input
                    id="pricing-amount"
                    type="number"
                    step="0.01"
                    value={pricing?.priceAmount || ''}
                    onChange={(e) => {
                      setPricing({
                        ...pricing,
                        priceAmount: e.target.value ? Number(e.target.value) : undefined,
                      });
                      setHasUnsavedChanges(true);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="9.95"
                  />
                </div>
                <div>
                  <label
                    htmlFor="pricing-currency"
                    className="block text-xs font-medium text-gray-600 mb-1"
                  >
                    Currency
                  </label>
                  <input
                    id="pricing-currency"
                    type="text"
                    value={pricing?.priceCurrency || ''}
                    onChange={(e) => {
                      setPricing({ ...pricing, priceCurrency: e.target.value || undefined });
                      setHasUnsavedChanges(true);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="USD"
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="pricing-unit"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Price Unit
                </label>
                <input
                  id="pricing-unit"
                  type="text"
                  value={pricing?.priceUnit || ''}
                  onChange={(e) => {
                    setPricing({ ...pricing, priceUnit: e.target.value || undefined });
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="e.g., megabyte, gigabyte"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-1 text-sm">
              {pricing &&
              (pricing.priceAmount !== undefined || pricing.priceCurrency || pricing.priceUnit) ? (
                <>
                  {pricing.priceAmount !== undefined && (
                    <div>
                      <span className="font-medium text-gray-600">Price:</span>{' '}
                      <span className="text-gray-900">
                        {pricing.priceCurrency || 'USD'} {pricing.priceAmount}
                        {pricing.priceUnit && ` per ${pricing.priceUnit}`}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <span className="text-gray-500">No pricing information</span>
              )}
            </div>
          )}
        </div>

        {/* Team */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Team</h3>
          {isEditable ? (
            <div className="space-y-3">
              {team.map((member, index) => (
                <div key={index} className="p-3 border border-gray-200 rounded-md bg-gray-50">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-xs font-semibold text-gray-700">Member {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newTeam = team.filter((_, i) => i !== index);
                        setTeam(newTeam);
                        setHasUnsavedChanges(true);
                      }}
                      className="text-red-600 hover:text-red-800 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label
                          htmlFor={`team-${index}-username`}
                          className="block text-xs font-medium text-gray-600 mb-1"
                        >
                          Username/Email
                        </label>
                        <input
                          id={`team-${index}-username`}
                          type="text"
                          value={member.username || ''}
                          onChange={(e) => {
                            const newTeam = [...team];
                            newTeam[index] = { ...member, username: e.target.value || undefined };
                            setTeam(newTeam);
                            setHasUnsavedChanges(true);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="username or email"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor={`team-${index}-name`}
                          className="block text-xs font-medium text-gray-600 mb-1"
                        >
                          Name
                        </label>
                        <input
                          id={`team-${index}-name`}
                          type="text"
                          value={member.name || ''}
                          onChange={(e) => {
                            const newTeam = [...team];
                            newTeam[index] = { ...member, name: e.target.value || undefined };
                            setTeam(newTeam);
                            setHasUnsavedChanges(true);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="Full name"
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor={`team-${index}-role`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Role
                      </label>
                      <input
                        id={`team-${index}-role`}
                        type="text"
                        value={member.role || ''}
                        onChange={(e) => {
                          const newTeam = [...team];
                          newTeam[index] = { ...member, role: e.target.value || undefined };
                          setTeam(newTeam);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="e.g., Owner, Data Steward, Data Scientist"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label
                          htmlFor={`team-${index}-date-in`}
                          className="block text-xs font-medium text-gray-600 mb-1"
                        >
                          Date In
                        </label>
                        <input
                          id={`team-${index}-date-in`}
                          type="date"
                          value={member.dateIn || ''}
                          onChange={(e) => {
                            const newTeam = [...team];
                            newTeam[index] = { ...member, dateIn: e.target.value || undefined };
                            setTeam(newTeam);
                            setHasUnsavedChanges(true);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor={`team-${index}-date-out`}
                          className="block text-xs font-medium text-gray-600 mb-1"
                        >
                          Date Out
                        </label>
                        <input
                          id={`team-${index}-date-out`}
                          type="date"
                          value={member.dateOut || ''}
                          onChange={(e) => {
                            const newTeam = [...team];
                            newTeam[index] = { ...member, dateOut: e.target.value || undefined };
                            setTeam(newTeam);
                            setHasUnsavedChanges(true);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor={`team-${index}-replaced-by`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Replaced By Username
                      </label>
                      <input
                        id={`team-${index}-replaced-by`}
                        type="text"
                        value={member.replacedByUsername || ''}
                        onChange={(e) => {
                          const newTeam = [...team];
                          newTeam[index] = {
                            ...member,
                            replacedByUsername: e.target.value || undefined,
                          };
                          setTeam(newTeam);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Username of replacement"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`team-${index}-comment`}
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Comment
                      </label>
                      <textarea
                        id={`team-${index}-comment`}
                        value={member.comment || ''}
                        onChange={(e) => {
                          const newTeam = [...team];
                          newTeam[index] = { ...member, comment: e.target.value || undefined };
                          setTeam(newTeam);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Free text comment"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setTeam([...team, {}]);
                  setHasUnsavedChanges(true);
                }}
                className="w-full px-3 py-2 text-sm border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
              >
                + Add Team Member
              </button>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              {team.length > 0 ? (
                team.map((member, index) => (
                  <div key={index} className="p-3 border border-gray-200 rounded-md">
                    <div className="font-medium text-gray-900 mb-1">
                      {member.name || member.username || `Member ${index + 1}`}
                    </div>
                    {member.role && (
                      <div className="text-gray-600 mb-1">
                        <span className="font-medium">Role:</span> {member.role}
                      </div>
                    )}
                    {member.username && member.username !== member.name && (
                      <div className="text-xs text-gray-500 mb-1">
                        <span className="font-medium">Username:</span> {member.username}
                      </div>
                    )}
                    <div className="flex gap-4 text-xs text-gray-500">
                      {member.dateIn && (
                        <span>
                          <span className="font-medium">Date In:</span> {member.dateIn}
                        </span>
                      )}
                      {member.dateOut && (
                        <span>
                          <span className="font-medium">Date Out:</span> {member.dateOut}
                        </span>
                      )}
                    </div>
                    {member.replacedByUsername && (
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">Replaced By:</span>{' '}
                        {member.replacedByUsername}
                      </div>
                    )}
                    {member.comment && <div className="text-gray-600 mt-1">{member.comment}</div>}
                  </div>
                ))
              ) : (
                <span className="text-gray-500">No team members defined</span>
              )}
            </div>
          )}
        </div>

        {/* SLA Information */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            Service Level Agreement (SLA)
          </h3>
          {isEditable ? (
            <div className="space-y-2">
              <div>
                <label
                  htmlFor="sla-latency"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Latency (ms)
                </label>
                <input
                  id="sla-latency"
                  type="number"
                  value={sla?.latency || ''}
                  onChange={(e) => {
                    setSLA({
                      ...sla,
                      latency: e.target.value ? Number(e.target.value) : undefined,
                    });
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Latency in milliseconds"
                />
              </div>
              <div>
                <label
                  htmlFor="sla-uptime"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Uptime (%)
                </label>
                <input
                  id="sla-uptime"
                  type="number"
                  min="0"
                  max="100"
                  value={sla?.uptime || ''}
                  onChange={(e) => {
                    setSLA({ ...sla, uptime: e.target.value ? Number(e.target.value) : undefined });
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Uptime percentage (0-100)"
                />
              </div>
              <div>
                <label
                  htmlFor="sla-response-time"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Response Time (ms)
                </label>
                <input
                  id="sla-response-time"
                  type="number"
                  value={sla?.response_time || ''}
                  onChange={(e) => {
                    setSLA({
                      ...sla,
                      response_time: e.target.value ? Number(e.target.value) : undefined,
                    });
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Response time in milliseconds"
                />
              </div>
              <div>
                <label
                  htmlFor="sla-error-rate"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Error Rate (%)
                </label>
                <input
                  id="sla-error-rate"
                  type="number"
                  min="0"
                  max="100"
                  value={sla?.error_rate || ''}
                  onChange={(e) => {
                    setSLA({
                      ...sla,
                      error_rate: e.target.value ? Number(e.target.value) : undefined,
                    });
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Error rate percentage (0-100)"
                />
              </div>
              <div>
                <label
                  htmlFor="sla-update-frequency"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Update Frequency
                </label>
                <input
                  id="sla-update-frequency"
                  type="text"
                  value={sla?.update_frequency || ''}
                  onChange={(e) => {
                    setSLA({ ...sla, update_frequency: e.target.value || undefined });
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="e.g., daily, hourly, real-time"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-1 text-sm">
              {sla &&
              (sla.latency !== undefined ||
                sla.uptime !== undefined ||
                sla.response_time !== undefined ||
                sla.error_rate !== undefined ||
                sla.update_frequency) ? (
                <>
                  {sla.latency !== undefined && (
                    <div>
                      <span className="font-medium text-gray-600">Latency:</span>{' '}
                      <span className="text-gray-900">{sla.latency} ms</span>
                    </div>
                  )}
                  {sla.uptime !== undefined && (
                    <div>
                      <span className="font-medium text-gray-600">Uptime:</span>{' '}
                      <span className="text-gray-900">{sla.uptime}%</span>
                    </div>
                  )}
                  {sla.response_time !== undefined && (
                    <div>
                      <span className="font-medium text-gray-600">Response Time:</span>{' '}
                      <span className="text-gray-900">{sla.response_time} ms</span>
                    </div>
                  )}
                  {sla.error_rate !== undefined && (
                    <div>
                      <span className="font-medium text-gray-600">Error Rate:</span>{' '}
                      <span className="text-gray-900">{sla.error_rate}%</span>
                    </div>
                  )}
                  {sla.update_frequency && (
                    <div>
                      <span className="font-medium text-gray-600">Update Frequency:</span>{' '}
                      <span className="text-gray-900">{sla.update_frequency}</span>
                    </div>
                  )}
                </>
              ) : (
                <span className="text-gray-500">No SLA information</span>
              )}
            </div>
          )}
        </div>

        {/* Custom Metadata - Key/Value Pairs */}
        {isEditable && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Custom Metadata</h3>
            <div className="space-y-2">
              {Object.entries(metadata).map(([key, value], index) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={key}
                    onChange={(e) => {
                      const newKey = e.target.value;
                      const entries = Object.entries(metadata);
                      entries[index] = [newKey, value];
                      setMetadata(Object.fromEntries(entries));
                      setHasUnsavedChanges(true);
                    }}
                    placeholder="Key"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <input
                    type="text"
                    value={typeof value === 'string' ? value : JSON.stringify(value)}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      // Try to parse as JSON, otherwise keep as string
                      let parsedValue: unknown = newValue;
                      try {
                        parsedValue = JSON.parse(newValue);
                      } catch {
                        // Keep as string
                      }
                      setMetadata({ ...metadata, [key]: parsedValue });
                      setHasUnsavedChanges(true);
                    }}
                    placeholder="Value"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const { [key]: _, ...rest } = metadata;
                      setMetadata(rest);
                      setHasUnsavedChanges(true);
                    }}
                    className="px-2 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                    title="Remove"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const newKey = `key_${Object.keys(metadata).length + 1}`;
                  setMetadata({ ...metadata, [newKey]: '' });
                  setHasUnsavedChanges(true);
                }}
                className="w-full px-3 py-2 text-sm border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
              >
                + Add Metadata
              </button>
            </div>
            {Object.keys(metadata).length === 0 && (
              <p className="mt-2 text-xs text-gray-500">
                Add custom key-value pairs for additional metadata
              </p>
            )}
          </div>
        )}

        {/* Data Quality Checks */}
        {isEditable && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Table-Level Quality Checks</h3>
            <div className="space-y-3">
              {/* 1. Table is not empty */}
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rowCountEnabled}
                    onChange={(e) => {
                      setRowCountEnabled(e.target.checked);
                      setHasUnsavedChanges(true);
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Table is not empty</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 pl-6">
                  Table must contain at least one row
                </p>
              </div>

              {/* 2. Row count within bounds */}
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rowCountBoundsEnabled}
                    onChange={(e) => {
                      setRowCountBoundsEnabled(e.target.checked);
                      setHasUnsavedChanges(true);
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Row count within bounds</span>
                </label>
                {rowCountBoundsEnabled && (
                  <div className="mt-2 pl-6 space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Date column
                      </label>
                      <select
                        value={rowCountBoundsDateColumn}
                        onChange={(e) => {
                          setRowCountBoundsDateColumn(e.target.value);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                      >
                        <option value="">Select a column...</option>
                        {(table.columns || [])
                          .filter((c) => !c.parent_column_id)
                          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                          .map((c) => (
                            <option key={c.id} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Min rows
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={rowCountBoundsMin}
                          onChange={(e) => {
                            setRowCountBoundsMin(parseInt(e.target.value, 10) || 0);
                            setHasUnsavedChanges(true);
                          }}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Max rows
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={rowCountBoundsMax}
                          onChange={(e) => {
                            setRowCountBoundsMax(parseInt(e.target.value, 10) || 100000);
                            setHasUnsavedChanges(true);
                          }}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Daily row count for current date should be between{' '}
                      {rowCountBoundsMin.toLocaleString()} and {rowCountBoundsMax.toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              {/* 3. Freshness check */}
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={freshnessEnabled}
                    onChange={(e) => {
                      setFreshnessEnabled(e.target.checked);
                      if (!e.target.checked) {
                        setFreshnessColumn('');
                        setFreshnessThreshold(1440);
                      }
                      setHasUnsavedChanges(true);
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Freshness check</span>
                </label>
                {freshnessEnabled && (
                  <div className="mt-2 pl-6 space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Timestamp column
                      </label>
                      <select
                        value={freshnessColumn}
                        onChange={(e) => {
                          setFreshnessColumn(e.target.value);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                      >
                        <option value="">Select a column...</option>
                        {(table.columns || [])
                          .filter((c) => !c.parent_column_id)
                          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                          .map((c) => (
                            <option key={c.id} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Must be greater than (minutes)
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={freshnessThreshold}
                        onChange={(e) => {
                          setFreshnessThreshold(parseInt(e.target.value, 10) || 1440);
                          setHasUnsavedChanges(true);
                        }}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Column-level checks (column exists, null ratio) are applied automatically per column
              on save.
            </p>
          </div>
        )}

        {/* Custom Quality Rules (JSON) */}
        {isEditable && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Custom Quality Rules (JSON)
            </h3>
            <textarea
              value={JSON.stringify(
                Array.isArray(qualityRules) && qualityRules.length === 0 ? {} : qualityRules,
                null,
                2
              )}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  setQualityRules(parsed);
                  setHasUnsavedChanges(true);
                } catch {
                  // Invalid JSON, don't update
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
              rows={4}
              placeholder='[{"type": "custom", "name": "my_check", "dimension": "accuracy"}]'
            />
            <p className="mt-1 text-xs text-gray-500">
              Additional quality rules beyond the managed checks above
            </p>
          </div>
        )}

        {/* Timestamps */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Timestamps</h3>
          <div className="space-y-1 text-sm">
            <div>
              <span className="font-medium text-gray-600">Created:</span>{' '}
              <span className="text-gray-900">{new Date(table.created_at).toLocaleString()}</span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Last Modified:</span>{' '}
              <span className="text-gray-900">
                {new Date(table.last_modified_at).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        {isEditable && (
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !hasUnsavedChanges}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Metadata'}
            </button>
          </div>
        )}
      </div>
    </DraggableModal>
  );
};
